import { ClientFactory } from "@/main/common/ClientFactory";
import type {GroupInviteRequestResultStatus, GroupInviteRequestRow} from "@/main/common/GroupInviteRequestTypes";
import { RequestCoalescer } from "@/main/common/RequestCoalescer";
import type {
    InviteUserToGroupResult,
    InviteUserToGroupResultStatus,
} from "@/main/common/GroupInviteCandidateTypes";
import {
    GroupInvitationDTO,
    GroupLeadershipAssignmentRequestLeadershipLevelEnum,
    UserProfileDTO,
    ViewerGroupInvitationDTO,
} from "@/generated-client";

export type GroupLeadershipLevel = "OWNER" | "ADMINISTRATOR" | "MODERATOR";
export type GroupInvitationAccessLevel = GroupLeadershipLevel | "MEMBER";

export type GroupLeaderSummary = UserProfileDTO & {
    leadershipLevel?: GroupLeadershipLevel;
};

export type GroupInvitationSummary = {
    invitationPublicId?: string;
    username?: string;
    profileImageUrl?: string;
    invitedByUsername?: string;
    invitedByProfileImageUrl?: string;
    leadershipLevel?: GroupLeadershipLevel | null;
    createdAt?: string;
    status?: string;
};

export type ViewerGroupInvitationSummary = {
    invitationPublicId?: string;
    invitedByUsername?: string;
    invitedByProfileImageUrl?: string;
    leadershipLevel?: GroupLeadershipLevel | null;
    createdAt?: string;
    status?: string;
};

function groupApi() {
    return ClientFactory.getAjaxGroupRestControllerApi();
}

/**
 * `GroupButtons` mounts more than once per group on the home page (inline discovery row + desktop
 * rail), and each mount used to issue its own viewer-invitation request — 39% of this endpoint's prod
 * traffic was same-session duplicates within 2s. Coalescing on the group id removes the duplicate
 * request without touching the duplicate mount. See
 * `designs/01-now/2026-07-24-home-discovery-duplicate-mount-design.md`.
 */
const viewerInvitationCoalescer = new RequestCoalescer<ViewerGroupInvitationSummary | null>();

function toGeneratedLeadershipLevel(level: GroupLeadershipLevel) {
    switch (level) {
        case "OWNER":
            return GroupLeadershipAssignmentRequestLeadershipLevelEnum.Owner;
        case "ADMINISTRATOR":
            return GroupLeadershipAssignmentRequestLeadershipLevelEnum.Administrator;
        case "MODERATOR":
            return GroupLeadershipAssignmentRequestLeadershipLevelEnum.Moderator;
    }
}

function toGroupLeadershipLevel(level?: string | null): GroupLeadershipLevel | null {
    switch (level) {
        case "OWNER":
        case "ADMINISTRATOR":
        case "MODERATOR":
            return level;
        default:
            return null;
    }
}

/** Narrows the wire value, defaulting to the pre-2026-08-20 meaning of a bodiless 200. */
function toInviteResultStatus(status?: string | null): InviteUserToGroupResultStatus {
    switch (status) {
        case "ADDED":
        case "ALREADY_IN_GROUP":
        case "ALREADY_INVITED":
            return status;
        default:
            return "INVITED";
    }
}

function toGroupInvitationSummary(invitation?: GroupInvitationDTO | null): GroupInvitationSummary {
    const invitationWithInviterAvatar = invitation as (GroupInvitationDTO & { invitedByProfileImageUrl?: string | null }) | null | undefined;
    return {
        invitationPublicId: invitation?.invitationPublicId,
        username: invitation?.username,
        profileImageUrl: invitation?.profileImageUrl,
        invitedByUsername: invitation?.invitedByUsername,
        invitedByProfileImageUrl: invitationWithInviterAvatar?.invitedByProfileImageUrl ?? undefined,
        leadershipLevel: toGroupLeadershipLevel(invitation?.leadershipLevel),
        createdAt: invitation?.createdAt,
        status: invitation?.status,
    };
}

function toViewerGroupInvitationSummary(invitation?: ViewerGroupInvitationDTO | null): ViewerGroupInvitationSummary | null {
    if (!invitation) {
        return null;
    }

    const invitationWithInviterAvatar = invitation as ViewerGroupInvitationDTO & { invitedByProfileImageUrl?: string | null };

    return {
        invitationPublicId: invitation.invitationPublicId,
        invitedByUsername: invitation.invitedByUsername,
        invitedByProfileImageUrl: invitationWithInviterAvatar.invitedByProfileImageUrl ?? undefined,
        leadershipLevel: toGroupLeadershipLevel(invitation.leadershipLevel),
        createdAt: invitation.createdAt,
        status: invitation.status,
    };
}

export const GroupGovernanceApi = {
    async assignLeadership(groupPublicId: string, username: string, leadershipLevel: Exclude<GroupLeadershipLevel, "OWNER">): Promise<void> {
        await groupApi().assignLeadership(groupPublicId, {
            username,
            leadershipLevel: toGeneratedLeadershipLevel(leadershipLevel),
        });
    },

    async removeLeadership(groupPublicId: string, username: string): Promise<void> {
        await groupApi().removeLeadership(groupPublicId, username);
    },

    /**
     * Sends the invitation and RETURNS WHAT IT DID.
     *
     * <p>The endpoint answered `void` until 2026-08-20; three cases that used to be thrown validation
     * errors — already a member, already invited, and a decline still inside the cooldown — are now
     * ordinary 200s carrying a status (invite-a-person design §6.2). A caller that ignores the body
     * therefore reports "Invitation sent" for all three and paints a phantom pending row.</p>
     *
     * <p>The generated client still types this operation's body as `void`, so the cast is where the new
     * contract enters the frontend. An older backend answering with no body reads as `INVITED`, which is
     * exactly what this endpoint meant before it carried a result at all — the safe direction, since the
     * worst case is a success message for a write that succeeded.</p>
     */
    async inviteUser(groupPublicId: string, username: string, accessLevel: GroupInvitationAccessLevel): Promise<InviteUserToGroupResultStatus> {
        const response = await groupApi().inviteUser(groupPublicId, {
            username,
            leadershipLevel: accessLevel === "MEMBER"
                ? undefined
                : toGeneratedLeadershipLevel(accessLevel),
        });
        viewerInvitationCoalescer.invalidate(groupPublicId);
        return toInviteResultStatus((response.data as InviteUserToGroupResult | undefined | null)?.status);
    },

    async listPendingInvitations(groupPublicId: string): Promise<GroupInvitationSummary[]> {
        const response = await groupApi().listPendingInvitations(groupPublicId);
        return (response.data ?? []).map(toGroupInvitationSummary);
    },

    getViewerPendingInvitation(groupPublicId: string): Promise<ViewerGroupInvitationSummary | null> {
        return viewerInvitationCoalescer.run(groupPublicId, async () => {
            const response = await groupApi().getViewerPendingInvitation(groupPublicId);
            return toViewerGroupInvitationSummary(response.data ?? null);
        });
    },

    // accept / decline / revoke are keyed by invitation id, not group id, so there is no single
    // coalescer key to drop — clearing a two-second map is cheaper than tracking that mapping.
    async acceptInvitation(invitationPublicId: string): Promise<void> {
        await groupApi().acceptInvitation(invitationPublicId);
        viewerInvitationCoalescer.clear();
    },

    async declineInvitation(invitationPublicId: string): Promise<void> {
        await groupApi().declineInvitation(invitationPublicId);
        viewerInvitationCoalescer.clear();
    },

    async revokeInvitation(invitationPublicId: string): Promise<void> {
        await groupApi().revokeInvitation(invitationPublicId);
        viewerInvitationCoalescer.clear();
    },

    // ---- Member invite requests with leader pre-approval (2026-09-02). These go through
    // ---- ClientFactory's hand-written wrappers rather than `groupApi()`, because the generated client
    // ---- does not carry the endpoints yet (AGENTS.md rule 6).

    async createInviteRequest(groupPublicId: string, username: string): Promise<GroupInviteRequestResultStatus> {
        const response = await ClientFactory.createGroupInviteRequest(groupPublicId, { username });
        // An older backend with no body would read as REQUESTED, which is wrong here in a way the
        // invite endpoint's equivalent default is not: claiming a request was filed when none was is a
        // silent no-op the member cannot see. Default to ALREADY_REQUESTED instead, whose message tells
        // them to look at the queue rather than that they succeeded.
        return response.data?.status ?? "ALREADY_REQUESTED";
    },

    async listInviteRequests(groupPublicId: string, page = 0): Promise<GroupInviteRequestRow[]> {
        const response = await ClientFactory.listGroupInviteRequests(groupPublicId, page);
        return response.data?.content ?? [];
    },

    async listOwnInviteRequests(page = 0): Promise<GroupInviteRequestRow[]> {
        const response = await ClientFactory.listOwnGroupInviteRequests(page);
        return response.data?.content ?? [];
    },

    async approveInviteRequest(requestPublicId: string): Promise<void> {
        await ClientFactory.approveGroupInviteRequest(requestPublicId);
        viewerInvitationCoalescer.clear();
    },

    async declineInviteRequest(requestPublicId: string, reason?: string): Promise<void> {
        await ClientFactory.declineGroupInviteRequest(requestPublicId, { reason });
    },

    async withdrawInviteRequest(requestPublicId: string): Promise<void> {
        await ClientFactory.withdrawGroupInviteRequest(requestPublicId);
    },

    resetForTests() {
        viewerInvitationCoalescer.clear();
    },
};
